import { isFilledText, legalApplicability, missingRequiredFields } from '@atlas/core-utils/work-order'
import {
	type AssetTypeCode,
	AxisCode,
	type DimensionUnit,
	LegalApplicability,
	MessageType,
	type RequiredWorkOrderField,
} from '@atlas/types'
import type { UpdateWorkOrderInput, WorkOrder } from '@/domain/work-order.types'
import { EMPTY_PACK, mainCopyTypeOf } from '@/domain/work-order-pack'

/** The Legal Content rows' applicability flags, as the pack stores them. */
export type LegalFlag =
	| 'mainCopyApplies'
	| 'legalTextApplies'
	| 'copyrightApplies'
	| 'aiLegalTextApplies'
	| 'isiApplies'
	| 'piApplies'

/** One Legal Content row: its caption, its flag, and the message type its text comes from. */
export interface LegalRowSpec {
	flag: LegalFlag
	label: string
	/** The brand message type the text is read from; null for the main copy and for PI, which no type holds. */
	messageType: MessageType | null
}

/** The rows in the Figma's order. */
export const LEGAL_ROWS: readonly LegalRowSpec[] = [
	{ flag: 'mainCopyApplies', label: 'Main copy / content', messageType: null },
	{ flag: 'legalTextApplies', label: 'Legal text', messageType: MessageType.DISCLAIMER },
	{ flag: 'copyrightApplies', label: 'Copyright', messageType: MessageType.COPYRIGHT },
	{ flag: 'aiLegalTextApplies', label: 'AI/Gen AI legal text', messageType: MessageType.AI_DISCLAIMER },
	{ flag: 'isiApplies', label: 'ISI', messageType: MessageType.DISCLAIMER },
	{ flag: 'piApplies', label: 'PI', messageType: null },
]

/** What the PM edits on the step, as the inputs hold it: text as typed, flags unset until touched. */
export interface PackForm {
	assetTypeCode: AssetTypeCode | null
	platform: string | null
	aspectRatio: string | null
	exactDimensions: string
	dimensionUnit: DimensionUnit | null
	sharepointFolderUrl: string
	mainCopy: string
	legalContentApplies: boolean | null
	flags: Record<LegalFlag, boolean | null>
	isCampaign: boolean
	campaignCode: string | null
}

/** What the PM changed on the form: a field absent here reads the order's current value. */
export type PackFormEdits = Partial<Omit<PackForm, 'flags'>> & { flags?: Partial<Record<LegalFlag, boolean | null>> }

/** The form the PM sees: the order as it stands now, with their edits on top. */
export const withEdits = (base: PackForm, edits: PackFormEdits): PackForm => ({
	...base,
	...edits,
	flags: { ...base.flags, ...edits.flags },
})

/** Two edits, the later winning, flags merged one by one. */
export const mergeEdits = (earlier: PackFormEdits, later: PackFormEdits): PackFormEdits => ({
	...earlier,
	...later,
	flags: { ...earlier.flags, ...later.flags },
})

/**
 * The edits still pending once a save of `saved` has landed: an edit equal to what was saved is
 * the order's value now, so it is dropped, and the field follows the order again (a refresh
 * from Workfront, say). An edit made while the save was in flight is kept.
 */
export function pruneEdits(edits: PackFormEdits, saved: PackForm): PackFormEdits {
	const { flags = {}, ...fields } = edits
	const pendingFields = Object.entries(fields).filter(
		([key, value]) => value !== saved[key as keyof Omit<PackForm, 'flags'>],
	)
	const pendingFlags = Object.entries(flags).filter(([flag, value]) => value !== saved.flags[flag as LegalFlag])
	return { ...Object.fromEntries(pendingFields), flags: Object.fromEntries(pendingFlags) }
}

/** The form as the order stores it. */
export function formFromOrder(wo: WorkOrder): PackForm {
	const pack = wo.pack ?? EMPTY_PACK
	return {
		assetTypeCode: wo.assetTypeCode,
		platform: wo.tuple?.[AxisCode.CHANNEL]?.[0] ?? null,
		aspectRatio: wo.aspectRatio ?? null,
		exactDimensions: pack.exactDimensions ?? '',
		dimensionUnit: pack.dimensionUnit,
		sharepointFolderUrl: pack.sharepointFolderUrl ?? '',
		mainCopy: pack.mainCopy?.content ?? '',
		legalContentApplies: pack.legalContentApplies,
		flags: Object.fromEntries(LEGAL_ROWS.map(({ flag }) => [flag, pack[flag]])) as Record<LegalFlag, boolean | null>,
		isCampaign: wo.campaignCode !== null,
		campaignCode: wo.campaignCode,
	}
}

/** Typed text, or null for blank, as the server stores it. */
const textOrNull = (text: string): string | null => (text.trim() ? text : null)

/** The pack fields the form changed. */
function packPatchOf(form: PackForm, saved: PackForm, wo: WorkOrder): UpdateWorkOrderInput['pack'] {
	const pack: NonNullable<UpdateWorkOrderInput['pack']> = {}
	if (textOrNull(form.exactDimensions) !== textOrNull(saved.exactDimensions))
		pack.exactDimensions = textOrNull(form.exactDimensions)
	if (form.dimensionUnit !== saved.dimensionUnit) pack.dimensionUnit = form.dimensionUnit
	if (form.sharepointFolderUrl.trim() !== saved.sharepointFolderUrl.trim())
		pack.sharepointFolderUrl = form.sharepointFolderUrl.trim() || null
	if (form.mainCopy !== saved.mainCopy) {
		pack.mainCopy = isFilledText(form.mainCopy)
			? { content: form.mainCopy, messageType: mainCopyTypeOf(wo.pack) }
			: null
	}
	if (form.legalContentApplies !== saved.legalContentApplies) pack.legalContentApplies = form.legalContentApplies
	for (const { flag } of LEGAL_ROWS) {
		if (form.flags[flag] !== saved.flags[flag]) pack[flag] = form.flags[flag]
	}
	return Object.keys(pack).length ? pack : undefined
}

/** The campaign the order should carry: none while the toggle is off. */
const campaignOf = (form: PackForm): string | null => (form.isCampaign ? form.campaignCode : null)

/**
 * The PATCH body that turns the stored order into the form, with only what changed: an
 * untouched field is left out, so Save Draft never rewrites a value nobody edited.
 */
export function patchFromForm(form: PackForm, wo: WorkOrder): UpdateWorkOrderInput {
	const saved = formFromOrder(wo)
	const patch: UpdateWorkOrderInput = {}
	if (form.assetTypeCode !== saved.assetTypeCode) patch.assetTypeCode = form.assetTypeCode
	if (form.platform !== saved.platform) patch.platform = form.platform
	if (form.aspectRatio !== saved.aspectRatio) patch.aspectRatio = form.aspectRatio
	if (campaignOf(form) !== wo.campaignCode) patch.campaignCode = campaignOf(form)
	const pack = packPatchOf(form, saved, wo)
	if (pack) patch.pack = pack
	return patch
}

/** Whether Save Draft has anything to send. */
export const isDirty = (form: PackForm, wo: WorkOrder): boolean => Object.keys(patchFromForm(form, wo)).length > 0

/**
 * The fields the Send Task guard lists that the form leaves empty: the same check the server
 * runs, on the form's values rather than the stored ones, except that a campaign toggled on
 * with none picked counts as missing its materials. The campaign's media plan is read by the
 * caller, since it is a file and not a form value.
 */
export function missingFromForm(
	form: PackForm,
	wo: WorkOrder,
	required: readonly RequiredWorkOrderField[],
	hasMediaPlan: boolean,
): RequiredWorkOrderField[] {
	const { [AxisCode.CHANNEL]: _channel, ...tuple } = wo.tuple ?? {}
	return missingRequiredFields(
		{
			...wo,
			assetTypeCode: form.assetTypeCode,
			tuple: form.platform ? { ...tuple, [AxisCode.CHANNEL]: [form.platform] } : tuple,
			hasMainCopy: isFilledText(form.mainCopy),
			// Saying the order is part of a campaign and naming none leaves the materials missing too.
			hasCampaignMaterials: !form.isCampaign || (!!form.campaignCode && hasMediaPlan),
		},
		required,
	)
}

/**
 * Whether a Legal Content row's toggle reads on: the PM's setting, or, until they set it,
 * whether its text applies (text other than `NA`).
 */
export const isRowApplicable = (form: PackForm, flag: LegalFlag, text: string | null): boolean =>
	form.flags[flag] ?? legalApplicability(text) === LegalApplicability.APPLICABLE

/** Whether the section's Yes toggle reads on; it starts on. */
export const isLegalContentOn = (form: PackForm): boolean => form.legalContentApplies ?? true

/** Why the typed SharePoint URL cannot be saved, or null when it can: blank, or an http(s) link. */
export function sharepointUrlError(text: string): string | null {
	const value = text.trim()
	if (!value) return null
	try {
		const { protocol } = new URL(value)
		if (protocol === 'http:' || protocol === 'https:') return null
	} catch {
		// Not a URL at all: reported below like any other link that is not http(s).
	}
	return 'The SharePoint folder must be a link starting with http:// or https://.'
}
